import React from 'react';
import { addYears, format } from 'date-fns';
import { toast } from 'react-toastify';
import NewProjectWizardCard from './NewProjectWizardCard';
import { formatFileSize, listProjectNdas, uploadProjectNda } from './projectSetupApi';
import {
    draftFromNda,
    emptyNdaDraft,
    filenameWithoutExtension,
    isNdaDraftReady,
    ndaFileType,
    ndaMetaLine,
    NDA_MODE,
    TEAM_SETUP_STEPS,
} from './ndaDraft';
import { isValidNdaExtensionToUpload } from '../../utils/commonUtils';
import styles from './css/index.module.css';
import fileDocIcon from './assets/file-doc.svg';

const OPTIONS = [
    {
        mode: NDA_MODE.NONE,
        title: 'No NDA',
        helper: 'Members can access the project after accepting their invitation.',
    },
    {
        mode: NDA_MODE.EXISTING,
        title: 'Select existing NDA',
        helper: 'Require members to accept an NDA already stored for this project.',
    },
    {
        mode: NDA_MODE.UPLOAD,
        title: 'Upload new NDA',
        helper: 'Upload a new NDA and use it for this invitation batch.',
    },
];

const ALLOWED_HINT = 'PDF, DOC or DOCX';

export default function SetupNda({
    projectId,
    draft = emptyNdaDraft(),
    onDraftChange,
    onContinue,
    onBack,
}) {
    const [mode, setMode] = React.useState(draft?.mode || NDA_MODE.NONE);
    const [selectedId, setSelectedId] = React.useState(draft?.ndaId || null);
    const [ndas, setNdas] = React.useState([]);
    const [loading, setLoading] = React.useState(Boolean(projectId));
    const [file, setFile] = React.useState(null);
    const [ndaName, setNdaName] = React.useState(draft?.name || '');
    const [saving, setSaving] = React.useState(false);
    const [dragging, setDragging] = React.useState(false);
    const fileInputRef = React.useRef(null);
    const localPreviewRef = React.useRef(null);

    React.useEffect(() => {
        let cancelled = false;
        if (!projectId) {
            setLoading(false);
            return undefined;
        }
        setLoading(true);
        listProjectNdas(projectId)
            .then(data => {
                if (cancelled) return;
                const items = Array.isArray(data) ? data : data?.results || [];
                setNdas(items);
            })
            .catch(() => {
                if (!cancelled) setNdas([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [projectId]);

    React.useEffect(
        () => () => {
            if (localPreviewRef.current) URL.revokeObjectURL(localPreviewRef.current);
        },
        [],
    );

    const updateDraft = next => {
        if (onDraftChange) onDraftChange(next);
    };

    const handleMode = nextMode => {
        setMode(nextMode);
        if (nextMode === NDA_MODE.NONE) {
            const next = emptyNdaDraft();
            updateDraft(next);
            return;
        }
        if (nextMode === NDA_MODE.EXISTING) {
            const selected = ndas.find(item => String(item.id) === String(selectedId));
            updateDraft(selected ? draftFromNda(selected) : { ...emptyNdaDraft(), mode: NDA_MODE.EXISTING });
        }
        if (nextMode === NDA_MODE.UPLOAD) {
            updateDraft({
                ...emptyNdaDraft(),
                mode: NDA_MODE.UPLOAD,
                ndaId: draft?.mode === NDA_MODE.UPLOAD ? draft.ndaId : null,
                name: ndaName || draft?.name || null,
            });
        }
    };

    const handleSelectExisting = item => {
        setSelectedId(item.id);
        updateDraft(draftFromNda(item));
    };

    const processFile = nextFile => {
        if (!nextFile) return;
        const ext = nextFile.name.split('.').pop();
        if (!isValidNdaExtensionToUpload(ext)) {
            toast.error(`Use a ${ALLOWED_HINT} file.`);
            return;
        }
        if (localPreviewRef.current) URL.revokeObjectURL(localPreviewRef.current);
        localPreviewRef.current = URL.createObjectURL(nextFile);
        setFile(nextFile);
        const nextName = ndaName || filenameWithoutExtension(nextFile.name);
        setNdaName(nextName);
        updateDraft({
            mode: NDA_MODE.UPLOAD,
            ndaId: null,
            ndaVersionId: null,
            name: nextName,
            documentUrl: localPreviewRef.current,
            fileType: ndaFileType(null, nextFile),
        });
    };

    const handleView = url => {
        if (!url) return;
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const currentDraft = React.useMemo(() => {
        if (mode === NDA_MODE.NONE) return emptyNdaDraft();
        if (mode === NDA_MODE.EXISTING) {
            const selected = ndas.find(item => String(item.id) === String(selectedId));
            return selected ? draftFromNda(selected) : { ...emptyNdaDraft(), mode: NDA_MODE.EXISTING };
        }
        return {
            mode: NDA_MODE.UPLOAD,
            ndaId: draft?.mode === NDA_MODE.UPLOAD ? draft.ndaId : null,
            ndaVersionId: draft?.mode === NDA_MODE.UPLOAD ? draft.ndaVersionId : null,
            name: ndaName || null,
            documentUrl: file ? localPreviewRef.current : draft?.documentUrl,
            fileType: file ? ndaFileType(null, file) : draft?.fileType,
        };
    }, [draft, file, mode, ndaName, ndas, selectedId]);

    const continueDisabled = !isNdaDraftReady(currentDraft, { hasFile: Boolean(file), name: ndaName });

    const handleContinue = async () => {
        if (saving || continueDisabled) return;
        if (mode === NDA_MODE.UPLOAD && !currentDraft.ndaId) {
            if (!projectId) {
                toast.error('Create the project before uploading an NDA.');
                return;
            }
            setSaving(true);
            try {
                const uploaded = await uploadProjectNda(projectId, file, {
                    name: ndaName.trim(),
                    expiry: format(addYears(new Date(), 3), 'yyyy-MM-dd'),
                });
                const next = draftFromNda(uploaded, NDA_MODE.UPLOAD);
                setNdas(prev => [uploaded, ...prev.filter(item => item.id !== uploaded.id)]);
                updateDraft(next);
                onContinue(next);
            } catch {
                toast.error('Could not upload this NDA. Please try again.');
            } finally {
                setSaving(false);
            }
            return;
        }
        updateDraft(currentDraft);
        onContinue(currentDraft);
    };

    return (
        <NewProjectWizardCard
            step={3}
            totalSteps={TEAM_SETUP_STEPS}
            accessWide
            title="NDA"
            subtitle="Choose whether invited members must accept an NDA before accessing this project."
            continueLabel="Continue to review"
            continueDisabled={continueDisabled}
            continueLoading={saving}
            onContinue={handleContinue}
            onBack={onBack}
            backLabel="Back"
            backInline
        >
            <div className={styles.ndaOptionStack}>
                {OPTIONS.map(option => {
                    const selected = mode === option.mode;
                    return (
                        <div key={option.mode} className={`${styles.ndaOption} ${selected ? styles.ndaOptionSelected : ''}`}>
                            <button type="button" className={styles.ndaOptionHeader} onClick={() => handleMode(option.mode)}>
                                <span className={`${styles.agRadio} ${selected ? styles.agRadioChecked : ''}`} aria-hidden="true">
                                    {selected ? <span className={styles.agRadioDot} /> : null}
                                </span>
                                <span>
                                    <span className={styles.ndaOptionTitle}>{option.title}</span>
                                    <span className={styles.ndaOptionHelper}>{option.helper}</span>
                                </span>
                            </button>

                            {selected && option.mode === NDA_MODE.EXISTING ? (
                                <div className={styles.ndaExistingList} role="list">
                                    {loading ? <p className={styles.ndaEmpty}>Loading NDAs…</p> : null}
                                    {!loading && !ndas.length ? <p className={styles.ndaEmpty}>No NDAs have been uploaded for this project yet.</p> : null}
                                    {ndas.map(item => {
                                        const itemSelected = String(item.id) === String(selectedId);
                                        return (
                                            <div key={item.id} className={styles.ndaExistingRow} role="listitem">
                                                <button type="button" className={styles.ndaExistingMain} onClick={() => handleSelectExisting(item)}>
                                                    <span className={`${styles.agRadio} ${itemSelected ? styles.agRadioChecked : ''}`} aria-hidden="true">
                                                        {itemSelected ? <span className={styles.agRadioDot} /> : null}
                                                    </span>
                                                    <span>
                                                        <span className={styles.ndaExistingName}>{item.name || filenameWithoutExtension(item.document)}</span>
                                                        <span className={styles.ndaExistingMeta}>{ndaMetaLine(item)}</span>
                                                    </span>
                                                </button>
                                                {item.document ? (
                                                    <button type="button" className={styles.ndaViewLink} onClick={() => handleView(item.document)}>
                                                        View →
                                                    </button>
                                                ) : null}
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : null}

                            {selected && option.mode === NDA_MODE.UPLOAD ? (
                                <div className={styles.ndaUploadBody}>
                                    {!file ? (
                                        <div
                                            className={`${styles.ndaDropzone} ${dragging ? styles.dropzoneDragging : ''}`}
                                            onClick={() => fileInputRef.current?.click()}
                                            onDragEnter={event => {
                                                event.preventDefault();
                                                setDragging(true);
                                            }}
                                            onDragOver={event => event.preventDefault()}
                                            onDragLeave={event => {
                                                event.preventDefault();
                                                setDragging(false);
                                            }}
                                            onDrop={event => {
                                                event.preventDefault();
                                                setDragging(false);
                                                processFile(event.dataTransfer.files?.[0]);
                                            }}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={event => {
                                                if (event.key === 'Enter' || event.key === ' ') fileInputRef.current?.click();
                                            }}
                                        >
                                            <p className={styles.ndaDropTitle}>Drag and drop or browse</p>
                                            <p className={styles.ndaDropHint}>{ALLOWED_HINT}</p>
                                            <button
                                                type="button"
                                                className={styles.ndaBrowse}
                                                onClick={event => {
                                                    event.stopPropagation();
                                                    fileInputRef.current?.click();
                                                }}
                                            >
                                                Browse files
                                            </button>
                                        </div>
                                    ) : (
                                        <div className={styles.ndaUploaded}>
                                            <div className={styles.fileListItem}>
                                                <div className={styles.fileListMeta}>
                                                    <img src={fileDocIcon} alt="" className={styles.fileListIcon} />
                                                    <span className={styles.fileName}>{file.name}</span>
                                                    <span className={styles.fileSize}>{formatFileSize(file.size)}</span>
                                                </div>
                                                <div className={styles.ndaFileActions}>
                                                    <button type="button" className={styles.ndaViewLink} onClick={() => handleView(localPreviewRef.current)}>
                                                        View
                                                    </button>
                                                    <button type="button" className={styles.ndaViewLink} onClick={() => fileInputRef.current?.click()}>
                                                        Replace
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={styles.fileRemove}
                                                        onClick={() => {
                                                            setFile(null);
                                                            if (localPreviewRef.current) URL.revokeObjectURL(localPreviewRef.current);
                                                            localPreviewRef.current = null;
                                                            updateDraft({ ...emptyNdaDraft(), mode: NDA_MODE.UPLOAD, name: ndaName });
                                                        }}
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            </div>
                                            <label className={styles.ndaNameField}>
                                                <span className={styles.fieldLabel}>
                                                    NDA name <span className={styles.ndaRequired}>*</span>
                                                </span>
                                                <input
                                                    className={styles.ndaNameInput}
                                                    value={ndaName}
                                                    onChange={event => {
                                                        setNdaName(event.target.value);
                                                        updateDraft({
                                                            ...currentDraft,
                                                            name: event.target.value,
                                                        });
                                                    }}
                                                />
                                                <span className={styles.ndaNameHint}>This NDA will be added to the project’s NDA list and can be reused for future invitations.</span>
                                            </label>
                                        </div>
                                    )}
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        className={styles.hiddenFileInput}
                                        accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                                        onChange={event => {
                                            processFile(event.target.files?.[0]);
                                            event.target.value = '';
                                        }}
                                    />
                                </div>
                            ) : null}
                        </div>
                    );
                })}
            </div>
            {mode !== NDA_MODE.NONE ? (
                <p className={styles.ndaBatchHint}>
                    The selected NDA will apply to everyone in this invitation batch. To use a different NDA for other
                    participants, send a separate invitation batch.
                </p>
            ) : null}
        </NewProjectWizardCard>
    );
}
